/**
 * Engagement trends — three line charts (cumulative students, active, attempts).
 * See docs/FeatureDesigns/teacher-stats.md §3.3.
 */

import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import type { TeacherStatsResponse } from '../../api/adminApi';

function formatTick(date: string, grain: 'daily' | 'weekly'): string {
    const d = new Date(date + 'T00:00:00Z');
    if (grain === 'weekly') {
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
    }
    return d.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' });
}

interface Props {
    stats: TeacherStatsResponse;
}

export function EngagementSection({ stats }: Props) {
    const { grain, cumulativeStudents, activeStudentsTrend, questionsAttemptedTrend } = stats;

    return (
        <section className="ts-section">
            <h3>Engagement</h3>
            <p className="ts-section-sub">
                Trends over the selected range. {grain === 'weekly' ? 'Weekly' : 'Daily'} buckets.
            </p>

            <Chart
                title="Total students"
                data={cumulativeStudents.map(p => ({ x: p.date, y: p.total }))}
                grain={grain}
                color="#0ea5e9"
            />
            <Chart
                title="Active students (visited)"
                data={activeStudentsTrend.map(p => ({ x: p.date, y: p.value }))}
                grain={grain}
                color="#10b981"
            />
            <Chart
                title="Questions attempted"
                data={questionsAttemptedTrend.map(p => ({ x: p.date, y: p.value }))}
                grain={grain}
                color="#3b82f6"
            />
        </section>
    );
}

interface ChartProps {
    title: string;
    data: { x: string; y: number }[];
    grain: 'daily' | 'weekly';
    color: string;
}

function Chart({ title, data, grain, color }: ChartProps) {
    return (
        <div className="ts-chart-card">
            <h4>{title}</h4>
            <ResponsiveContainer width="100%" height={220}>
                <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                    <XAxis
                        dataKey="x"
                        tickFormatter={(v) => formatTick(v as string, grain)}
                        tick={{ fontSize: 11 }}
                        stroke="#9ca3af"
                    />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="#9ca3af" />
                    <Tooltip
                        labelFormatter={(label) => formatTick(String(label), grain)}
                        formatter={(value) => [value, title]}
                        contentStyle={{
                            background: 'var(--bg-primary)',
                            border: '1px solid var(--border-color)',
                            borderRadius: '8px',
                            color: 'var(--text-primary)',
                        }}
                        labelStyle={{ color: 'var(--text-primary)' }}
                        itemStyle={{ color: 'var(--text-primary)' }}
                    />
                    <Line type="monotone" dataKey="y" stroke={color} strokeWidth={2} dot={false} />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
}
